import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Star, Camera, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ValidationPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [testimonial, setTestimonial] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  useEffect(() => { fetchClaim(); }, [claimId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClaim = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/my-claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundClaim = response.data.find(c => c.claim_id === claimId);
      if (foundClaim) setClaim(foundClaim);
    } catch (error) {
      // error handled by toast
      toast.error('Erro ao carregar cupom');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 5) { toast.error('Máximo de 5 fotos'); return; }
    files.forEach(file => {
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX_FILE_SIZE) { toast.error('Foto muito grande. Máximo 5MB.'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotos(prev => [...prev, reader.result]);
        setPhotoPreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { toast.error('Por favor, selecione uma avaliação'); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${BACKEND_URL}/api/validations`,
        { claim_id: claimId, testimonial, rating, photos },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const basePoints = 10 + (rating * 2) + (photos.length * 5);
      toast.success(`Compra validada! +${basePoints} pontos adicionados.`);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      // error handled by toast
      toast.error('Erro ao validar compra');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500"></div>
          <p className="mt-4 text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!claim || claim.is_validated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="hero-card"><CardContent className="py-12 text-center">
          <p className="text-slate-200">{claim?.is_validated ? 'Este cupom já foi validado' : 'Cupom não encontrado'}</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Voltar ao Dashboard</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>

        <Card data-testid="validation-form-card" className="hero-card">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-white">Validar Compra</CardTitle>
            <p className="text-slate-200">{claim.coupon?.title}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white/10 border border-white/20 rounded-md p-4">
                <p className="text-sm text-slate-200 mb-1"><strong>Código:</strong></p>
                <p className="font-mono text-lg font-bold text-white">{claim.code}</p>
              </div>

              <div>
                <Label htmlFor="rating" className="text-white">Avaliação *</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} type="button" onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)} onMouseLeave={() => setHoveredRating(0)}
                      className="focus:outline-none" data-testid={`star-${star}`}>
                      <Star className={`h-8 w-8 ${star <= (hoveredRating || rating) ? 'fill-white text-white' : 'text-slate-400'} transition-colors`} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="testimonial" className="text-white">Depoimento *</Label>
                <Textarea id="testimonial" value={testimonial} onChange={(e) => setTestimonial(e.target.value)}
                  placeholder="Conte como foi sua experiência com este cupom..." rows={5} required
                  data-testid="testimonial-input" className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-slate-300" />
              </div>

              {/* Photo Upload */}
              <div>
                <Label className="text-white flex items-center gap-2"><Camera className="h-4 w-4" /> Fotos da Compra (opcional, +5 pontos cada)</Label>
                <Input type="file" accept="image/*" multiple onChange={handlePhotoSelect} disabled={photos.length >= 5}
                  data-testid="photo-upload-input" className="mt-2" />
                <p className="text-xs text-slate-300 mt-1">{photos.length}/5 fotos selecionadas</p>
                {photoPreviews.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-3">
                    {photoPreviews.map((preview, idx) => (
                      <div key={idx} className="relative group aspect-square overflow-hidden rounded-md border border-white/20">
                        <img src={preview} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                          data-testid={`remove-photo-${idx}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white/10 border border-white/20 rounded-md p-4">
                <h3 className="font-bold text-white mb-2">Ganhe Pontos:</h3>
                <ul className="text-sm text-slate-200 space-y-1">
                  <li>+10 pontos pela validação</li>
                  <li>+2 pontos por estrela de avaliação {rating > 0 && <span className="text-green-300">(+{rating * 2})</span>}</li>
                  <li>+5 pontos por foto enviada {photos.length > 0 && <span className="text-green-300">(+{photos.length * 5})</span>}</li>
                </ul>
                {rating > 0 && (
                  <p className="text-white font-bold mt-2 pt-2 border-t border-white/20">
                    Total: +{10 + (rating * 2) + (photos.length * 5)} pontos
                  </p>
                )}
              </div>

              <Button type="submit" disabled={submitting}
                className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 border border-white/30"
                data-testid="submit-validation-btn">
                {submitting ? 'Validando...' : 'Validar Compra'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
